import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../shared/services/auth.service';
import { ReservaGgrService } from './reserva-ggr.service';
import { TableTransactionsReservaComponent } from '../../table-transactions-reserva/table-transactions-reserva.component';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-reserva-ggr',
  templateUrl: './reserva-ggr.component.html',
  styleUrls: ['./reserva-ggr.component.scss'],
})
export class ReservaGgrComponent {
  aReceber: number = 0;
  recebimento: number = 0;
  ticketMedioRecebimento: number = 0;
  conversao: number = 0;
  saque: number = 0;
  saquePendente: number = 0;
  saldoFinal: number = 0;
  quantidadeDeposito: number = 0;
  quantidadeSaque: number = 0;

  listsAccountTef: { id: string; description: string; originCurrency?: string }[] = [];

  extrato = new FormGroup({
    tipo: new FormControl<string>(''),
    status: new FormControl<string>(''),
    tipoBusca: new FormControl<string>(''),
    origem: new FormControl<string>(''),
    aplicacao: new FormControl<string>(''),
    responsavel: new FormControl<string>(''),
    tefAccount: new FormControl<string>(''),
    initialDate: new FormControl<Date | null>(
      null,
      { validators: [Validators.required], updateOn: 'blur' }
    ),
    endDate: new FormControl<Date | null>(
      null,
      { validators: [Validators.required], updateOn: 'blur' }
    ),
  });

  transactionTypes = [
    { name: 'common.general', value: '' },
    { name: 'common.deposit', value: 'DEPOSITO' },
    { name: 'common.withdraw', value: 'SAQUE' },
  ];

  statusList: any[] = [
    { name: 'Todos', value: '' },
    { name: 'Pendente', value: 'PENDENTE' },
    { name: 'Realizado', value: 'REALIZADO' },
    { name: 'Analise', value: 'ANALISE' },
  ];

  originList = [
    { name: 'Todos', value: '' },
    { name: 'Pix', value: 'PIX' },
    { name: 'Comissão', value: 'COMISSÃO' },
    { name: 'Cripto', value: 'CRIPTO' },
    { name: 'Taxa Bancária', value: 'TAXA BANCÁRIA' },
    { name: 'Reembolso', value: 'REEMBOLSO' },
    { name: 'Tef', value: 'TEF' }
  ];

  applicationList = [
    { name: 'Todos', value: '' },
    { name: 'common.web', value: 'WEB' },
    { name: 'common.api', value: 'API' }
  ];

  tipoBuscaList: any[] = [
    { name: 'common.description', value: 'personName' },
    { name: 'common.id', value: 'id' },
    { name: 'common.idempotencyKey', value: 'idempotencyKey' },
    { name: 'common.ssn', value: 'cpf' },
  ];

  public isFilterColapsed = false;
  public isSubmitted = false;
  public isLoaded: boolean = false;

  private accountTimezoneOffsetMinutes: number | null = null;
  private originCurrency: string = 'BRL';

  @ViewChild(TableTransactionsReservaComponent, { static: true })
  table!: TableTransactionsReservaComponent;

  constructor(
    private reservaGgrService: ReservaGgrService,
    private authService: AuthService,
    private toastr: ToastrService
  ) {
    this.extrato.controls.status.setValue('REALIZADO');
    this.extrato.controls.tipoBusca.setValue('personName');

    const originCurrency = (this.authService.getOriginCurrency?.() || '').toUpperCase();
    this.originCurrency = originCurrency || 'BRL';
  }

  private parseTimezoneOffset(tz: string | null | undefined): number | null {
    if (!tz) return null;

    const m = /^([+-]?)(\d{1,2}):(\d{2})$/.exec(tz.trim());
    if (!m) return null;

    const sign = m[1] === '-' ? 1 : -1;
    const hours = Number(m[2]);
    const minutes = Number(m[3]);

    return sign * (hours * 60 + minutes);
  }

  private nowInAccountTz(): Date {
    const now = new Date();

    if (this.accountTimezoneOffsetMinutes == null) {
      return now;
    }

    const browserOffset = now.getTimezoneOffset();
    const diffMinutes = this.accountTimezoneOffsetMinutes - browserOffset;

    return new Date(now.getTime() - diffMinutes * 60000);
  }

  private formatSeconds(v: any): Date {
    const d = this.asDate(v);
    const copy = new Date(d);
    copy.setSeconds(59, 999);
    return copy;
  }

  private buildDefaultRange(hasFlag: boolean): { start: Date; end: Date } {
    const origin = (this.originCurrency || 'BRL').toUpperCase();

    if (origin === 'BRL') {
      const now = new Date();
      const tempEnd = new Date(now);

      const start = hasFlag
        ? (() => {
            const d = new Date(now.getTime() - 15 * 60 * 1000);
            d.setSeconds(0, 0);
            return d;
          })()
        : (() => {
            const d = new Date(now);
            d.setHours(0, 0, 0, 0);
            return d;
          })();

      const end = this.formatSeconds(tempEnd);
      return { start, end };
    }

    const nowAcc = this.nowInAccountTz();
    const tempEnd = new Date(nowAcc);

    const start = hasFlag
      ? (() => {
          const d = new Date(nowAcc.getTime() - 15 * 60 * 1000);
          d.setSeconds(0, 0);
          return d;
        })()
      : (() => {
          const d = new Date(nowAcc);
          d.setHours(0, 0, 0, 0);
          return d;
        })();

    const end = this.formatSeconds(tempEnd);
    return { start, end };
  }

  async ngOnInit(): Promise<void> {
    try {
      const baseAccount = await firstValueFrom(this.authService.getAccount());
      const reservaAccount = await this.reservaGgrService.getAccount();

      const hasFlag = !!baseAccount?.featureFlagNewParam;

      this.originCurrency =
        ((baseAccount as any)?.originCurrency ||
          this.authService.getOriginCurrency?.() ||
          this.originCurrency ||
          'BRL').toUpperCase();

      this.accountTimezoneOffsetMinutes = this.parseTimezoneOffset(
        (baseAccount as any)?.timezone
      );

      const { start, end } = this.buildDefaultRange(hasFlag);

      this.extrato.controls.initialDate.setValue(start);
      this.extrato.controls.endDate.setValue(end);

      const isGeneric = reservaAccount?.accountGeneric === true;

      if (!isGeneric && Array.isArray(reservaAccount.listsAccountTef)) {
        this.listsAccountTef = reservaAccount.listsAccountTef.map((it: any) => ({
          id: it.id,
          description: it.description,
          originCurrency: it.originCurrency,
        }));

        if (!this.extrato.controls.tefAccount.value && this.listsAccountTef.length) {
          this.extrato.controls.tefAccount.setValue(this.listsAccountTef[0].id);
        }
      } else {
        this.listsAccountTef = [];
        this.extrato.controls.tefAccount.setValue('');
      }

      this.buscar();
    } catch {
      const now = new Date();
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);

      this.extrato.controls.initialDate.setValue(start);
      this.extrato.controls.endDate.setValue(now);

      this.buscar();
      this.toastr.error('Erro ao buscar contas reserva');
    }
  }

  private toLocalIso(v: any): string {
    const dt = this.asDate(v);
    const pad = (n: number) => String(n).padStart(2, '0');
    const y = dt.getFullYear();
    const m = pad(dt.getMonth() + 1);
    const d = pad(dt.getDate());
    const hh = pad(dt.getHours());
    const mm = pad(dt.getMinutes());
    const ss = pad(dt.getSeconds());
    return `${y}-${m}-${d}T${hh}:${mm}:${ss}`;
  }

  private toNgb(v: any) {
    const d = this.asDate(v);
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }

  private asDate(v: any): Date {
    return v?.toDate ? v.toDate() : (v as Date);
  }

  loadScreen() {
    this.isLoaded = false;

    const accId = (this.extrato.controls.tefAccount.value || '').trim();

    if (!accId) {
      this.aReceber = 0;
      this.recebimento = 0;
      this.ticketMedioRecebimento = 0;
      this.conversao = 0;
      this.saque = 0;
      this.saquePendente = 0;
      this.saldoFinal = 0;
      this.quantidadeDeposito = 0;
      this.quantidadeSaque = 0;
      this.isLoaded = true;
      return;
    }

    const start = this.extrato.controls.initialDate.value!;
    const tempEnd = this.extrato.controls.endDate.value!;
    const end = this.formatSeconds(tempEnd);

    const dateInitialFormat = this.toLocalIso(start);
    const dateFinalFormat = this.toLocalIso(end);

    const origin = this.extrato.controls.origem.value!;
    const application = this.extrato.controls.aplicacao.value!;
    const personName = this.extrato.controls.responsavel.value!;
    const searchType = this.extrato.controls.tipoBusca.value!;

    this.getEnum();

    this.reservaGgrService
      .getDashBoardExtratoValues(
        dateInitialFormat,
        dateFinalFormat,
        origin,
        application,
        personName,
        searchType,
        accId
      )
      .subscribe(value => {
        this.aReceber = value.aReceber;
        this.recebimento = value.recebimento;
        this.ticketMedioRecebimento = value.ticketMedioRecebimento;
        this.conversao = value.conversao;
        this.saque = value.saque;
        this.saquePendente = value.saquePendente;
        this.saldoFinal = value.saldoFinal;
        this.quantidadeDeposito = value.quantidadeDeposito;
        this.quantidadeSaque = value.quantidadeSaque;
        this.isLoaded = true;
      });
  }

  getEnum(): void {
    this.reservaGgrService.getEnum()
      .then(resp => {
        this.originList = [
          { name: 'Todos', value: '' },
          ...resp.values.map((v: string) => ({ name: v, value: v }))
        ];
      });
  }

  buscar() {
    this.isSubmitted = true;
    this.isLoaded = false;

    const start = this.extrato.controls.initialDate.value!;
    const tempEnd = this.extrato.controls.endDate.value!;
    const end = this.formatSeconds(tempEnd);

    if (start > end) {
      this.toastr.error(
        'Data inicial deve ser menor ou igual a data final!',
        'Erro',
        {
          timeOut: 3000,
          positionClass: 'toast-top-right',
        }
      );
      this.isLoaded = true;
      return;
    }

    if (this.extrato.invalid) {
      this.isLoaded = true;
      return;
    }

    this.table.page = 1;
    this.table.dataInitial = this.toNgb(start);
    this.table.dataFinal = this.toNgb(end);

    this.table.selectedStatus = this.extrato.controls.status.value!;
    this.table.transactionType = this.extrato.controls.tipo.value!;
    this.table.selectedOrigin = this.extrato.controls.origem.value!;
    this.table.selectedApplciation = this.extrato.controls.aplicacao.value!;
    this.table.personName = this.extrato.controls.responsavel.value!;
    this.table.searchType = this.extrato.controls.tipoBusca.value!;

    const tefId = this.extrato.controls.tefAccount.value!;
    this.table.accountIdGeneric = tefId;

    const hit = this.listsAccountTef.find(x => x.id === tefId);
    this.table.accountOriginCurrency = hit?.originCurrency || '';

    this.table.startDateIso = this.toLocalIso(start);
    this.table.endDateIso = this.toLocalIso(end);

    this.table.getTransactions();
    this.loadScreen();

    this.isSubmitted = false;
  }

  filterColapse() {
    this.isFilterColapsed = !this.isFilterColapsed;
  }

  downloadFile() {
    const start = this.extrato.controls.initialDate.value!;
    const end = this.extrato.controls.endDate.value!;

    this.reservaGgrService
      .getCsvFile(
        start,
        end,
        this.extrato.controls.tipo.value!,
        this.extrato.controls.status.value!
      )
      .subscribe((value) => {
        const csvContent = decodeURIComponent(escape(atob(value)));
        const a = document.createElement('a');
        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        const url = window.URL.createObjectURL(blob);
        const company = this.authService.getCompany();
        a.href = url;
        a.download = `${company}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      });
  }
}
